import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { demeterMarketIndicatorsApi } from '../../../../../Apis/Apis';
import {
    DemeterFilterTimeSpan,
    GetMarketIndicatorFactorResponse,
    GetMarketIndicatorResponse,
    MarketIndicatorTemplateType,
    MarketIndicatorTransformationType,
} from '../../../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../../../Layouts/NavigationRoutes';
import useApiWithoutAutoExecute from '../../../../Apis/Hooks/useApiWithoutAutoExecute';
import chartService from '../../../../Components/Charts/ChartService';
import MarketIndicatorChart from '../../../../Components/Charts/MarketIndicator/MarketIndicatorChart';
// eslint-disable-next-line max-len
import MarketIndicatorAverageSeasonalChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorAverageSeasonal/MarketIndicatorAverageSeasonalChart';
import MarketIndicatorCombinationChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorCombination/MarketIndicatorCombinationChart';
import MarketIndicatorDecileChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorDecile/MarketIndicatorDecileChart';
import MarketIndicatorForecastChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorForecast/MarketIndicatorForecastChart';
import MarketIndicatorHistoricalChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorHistorical/MarketIndicatorHistoricalChart';
import MarketIndicatorSeasonalChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorSeasonal/MarketIndicatorSeasonalChart';
import MarketIndicatorTechnicalChart from '../../../../Components/Charts/MarketIndicators/MarketIndicatorTechnical/MarketIndicatorTechnicalChart';
import LinkButton, { LinkButtonType } from '../../../../Components/Form/Buttons/LinkButton';
import LoadingSpinner from '../../../../Components/LoadingSpinner/LoadingSpinner';
import useSearchParameters from '../../../../Components/Navigation/Hooks/useSearchParametersHook';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import {
    AverageSeasonalParameters,
    DecileParameters,
    ForecastParameters,
    HistoricalParameters,
    ManualEntryParameters,
    SeasonalParameters,
    TechnicalParameters,
    UpsertMarketIndicatorFactorRequest,
} from '../MarketIndicatorsManagementDefinitions';
import styles from './MarketIndicatorFactorPage.module.scss';

interface IMarketIndicatorFactorChartsProps {
    upsertMarketIndicatorFactorRequest: UpsertMarketIndicatorFactorRequest;
    parentUpsertMarketIndicatorFactorRequest?: UpsertMarketIndicatorFactorRequest;
    handleSave: () => void;
    handleSaveAndReturn: () => void;
    savingDisabled?: boolean;
    getMarketIndicatorResponse: GetMarketIndicatorResponse | undefined;
}

const allowedTransformationTypeYaxisOverrides = [
    MarketIndicatorTransformationType.YearOverYearChange,
    MarketIndicatorTransformationType.MonthOverMonthChange,
    MarketIndicatorTransformationType.WeekOverWeekChange,
    MarketIndicatorTransformationType.DayOverDayChange,
    MarketIndicatorTransformationType.RelativeStrengthIndex,
];

const MarketIndicatorFactorCharts: React.FC<IMarketIndicatorFactorChartsProps> = (props: IMarketIndicatorFactorChartsProps) => {
    // Application hooks.
    const [translations] = useLanguage();
    const navigate = useNavigate();
    const { marketIndicatorGuid } = useParams();
    const [searchParameters] = useSearchParameters();
    const navigateBackToMarketIndicators = () => navigate(`${NavigationRoutes.AdministrationMarketIndicators}/${marketIndicatorGuid}/edit`);

    const [testChartsButtonActivated, setTestChartsButtonActivated] = useState<boolean>();

    // For combination template.
    useEffect(() => {
        setTestChartsButtonActivated(false);
    }, [searchParameters?.tab]);

    // If we change subfactors or load the page, get initial test for our main market indicator chart.
    useEffect(() => {
        if (!props.upsertMarketIndicatorFactorRequest?.marketIndicatorFactorGuid) {
            return;
        }

        refreshRunTestMarketIndicatorFactor();
    }, [
        props.upsertMarketIndicatorFactorRequest?.marketIndicatorFactorGuid,
        props.upsertMarketIndicatorFactorRequest?.subFactors,
        props.getMarketIndicatorResponse?.marketIndicator?.numberOfYears,
        searchParameters.tab,
    ]);

    const [getTestMarketIndicatorFactorDataLoading, refreshGetTestMarketIndicatorFactorData, getTestMarketIndicatorFactorDataResponse] =
        useApiWithoutAutoExecute(() => demeterMarketIndicatorsApi.getTestMarketIndicatorFactorData(props.upsertMarketIndicatorFactorRequest));

    const [runTestMarketIndicatorFactorLoading, refreshRunTestMarketIndicatorFactor, runTestMarketIndicatorFactorResponse] = useApiWithoutAutoExecute(
        () =>
            demeterMarketIndicatorsApi.runTestMarketIndicatorFactor({
                leadingIndicatorType: props.upsertMarketIndicatorFactorRequest?.leadingIndicatorType!,
                region: props.upsertMarketIndicatorFactorRequest?.region,
                subRegion: props.upsertMarketIndicatorFactorRequest?.subRegion,
                commodity: props.upsertMarketIndicatorFactorRequest?.commodity,
                extraParameters: props.upsertMarketIndicatorFactorRequest?.extraParameters || undefined,
                dataFrequency: props.upsertMarketIndicatorFactorRequest?.dataFrequency,
                templateType: props.upsertMarketIndicatorFactorRequest?.templateType!,
                parameters: props.upsertMarketIndicatorFactorRequest?.parameters,
                correlation: props.upsertMarketIndicatorFactorRequest?.correlation!,
                numberOfYears: props.getMarketIndicatorResponse?.marketIndicator?.numberOfYears!,
                asOfDate: props.getMarketIndicatorResponse?.marketIndicator?.asOfDate!,
                subFactorGuids: isCombinationParent ? props.parentUpsertMarketIndicatorFactorRequest?.subFactors!.map((y) => y.marketIndicatorFactorGuid) : [],
                dataCombinationType: props.upsertMarketIndicatorFactorRequest?.dataCombinationType,
                dataCombinationDisplayUnits: props.upsertMarketIndicatorFactorRequest?.dataCombinationDisplayUnits,
                currency: props.upsertMarketIndicatorFactorRequest?.currency,
                unitOfMeasure: props.upsertMarketIndicatorFactorRequest?.unitOfMeasure,
                transformationType: props.upsertMarketIndicatorFactorRequest?.transformationType,
                transformationValue: props.upsertMarketIndicatorFactorRequest?.transformationValue,
                secondaryExtraParameters: props.upsertMarketIndicatorFactorRequest?.secondaryExtraParameters,
                secondaryRegion: props.upsertMarketIndicatorFactorRequest?.secondaryRegion,
                secondarySubRegion: props.upsertMarketIndicatorFactorRequest?.secondarySubRegion,
                secondaryCommodity: props.upsertMarketIndicatorFactorRequest?.secondaryCommodity,
                secondaryDataFrequency: props.upsertMarketIndicatorFactorRequest?.secondaryDataFrequency,
                secondaryLeadingIndicatorType: props.upsertMarketIndicatorFactorRequest?.secondaryLeadingIndicatorType,
                secondaryTransformationType: props.upsertMarketIndicatorFactorRequest?.secondaryTransformationType,
                secondaryTransformationValue: props.upsertMarketIndicatorFactorRequest?.secondaryTransformationValue,
                secondaryCurrency: props.upsertMarketIndicatorFactorRequest?.secondaryCurrency,
                secondaryUnitOfMeasure: props.upsertMarketIndicatorFactorRequest?.secondaryUnitOfMeasure,
            }),
        {
            showResponseErrorMessage: true,
        },
    );

    // General constants
    const isCombinationParent =
        searchParameters.tab === 'parent' ||
        (props.upsertMarketIndicatorFactorRequest?.templateType === MarketIndicatorTemplateType.Combination &&
            !props.parentUpsertMarketIndicatorFactorRequest?.marketIndicatorFactorGuid);

    const currentMarketIndicatorSubfactors = props.parentUpsertMarketIndicatorFactorRequest?.subFactors;
    const testChartButtonActiveAndValidGetTestFactorResponse = testChartsButtonActivated && !!getTestMarketIndicatorFactorDataResponse;
    const testButtonDisabled =
        (isCombinationParent && !currentMarketIndicatorSubfactors) || getTestMarketIndicatorFactorDataLoading || runTestMarketIndicatorFactorLoading;

    // Define template types.
    const templateTypeIsAverageSeasonal = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.AverageSeasonal;
    const templateTypeIsAverageCount = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.AverageCount;
    const templateTypeIsAverageCountOrAverageSeasonal = templateTypeIsAverageCount || templateTypeIsAverageSeasonal;
    const templateTypeIsCombination = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Combination;
    const templateTypeIsDecile = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Decile;
    const templateTypeIsForecast = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Forecast;
    const templatTypeIsHistorical = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Historical;
    const templateTypeIsManualEntry = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.ManualEntry;
    const templateTypeIsSeasonal = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Seasonal;
    const templateTypeIsSlidingWindowSeasonal = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.SlidingWindowSeasonal;
    const templateTypeIsTechnical = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Technical;
    const templateTypeIsSpeculative = props.upsertMarketIndicatorFactorRequest.templateType === MarketIndicatorTemplateType.Speculative;

    const transformationType = useMemo(() => {
        if (
            !props.upsertMarketIndicatorFactorRequest.transformationType ||
            !allowedTransformationTypeYaxisOverrides.find((x) => x === props.upsertMarketIndicatorFactorRequest.transformationType)
        ) {
            return undefined;
        }

        return translations.marketIndicatorTransformationType[props.upsertMarketIndicatorFactorRequest.transformationType];
    }, [translations, props.upsertMarketIndicatorFactorRequest.transformationType]);

    const hasRestrictedDataSource = chartService.isRestrictedDataSource(...(getTestMarketIndicatorFactorDataResponse?.dataSourceTags ?? []));

    const chartDisplayName = useMemo(
        () => props.upsertMarketIndicatorFactorRequest?.chartDisplayName || props.upsertMarketIndicatorFactorRequest?.displayName,
        [props.upsertMarketIndicatorFactorRequest],
    );

    const secondaryRunTestMarketIndicatorFactorResponse = useMemo(() => {
        if (!props.getMarketIndicatorResponse?.marketIndicator?.numberOfYears || !getTestMarketIndicatorFactorDataResponse?.rows || !templateTypeIsTechnical) {
            return undefined;
        }

        const startDate = moment().subtract(props.getMarketIndicatorResponse.marketIndicator.numberOfYears, 'year').toDate();

        return {
            ...getTestMarketIndicatorFactorDataResponse,
            rows: getTestMarketIndicatorFactorDataResponse?.rows?.filter((x) => new Date(x.asOfDate) >= startDate) ?? [],
            additionalData: getTestMarketIndicatorFactorDataResponse.additionalData,
            unitOfMeasure: getTestMarketIndicatorFactorDataResponse?.unitOfMeasure,
            currency: getTestMarketIndicatorFactorDataResponse?.currency,
        };
    }, [runTestMarketIndicatorFactorResponse, getTestMarketIndicatorFactorDataResponse, props.getMarketIndicatorResponse, templateTypeIsTechnical]);

    return (
        <div className={styles.indicator_add_and_edit_markets_chart_and_controls}>
            <div className={styles.indicator_add_and_edit_action_buttons_row}>
                <div className={styles.indicator_add_and_edit_action_buttons_test}>
                    <LinkButton
                        onClick={() => {
                            refreshRunTestMarketIndicatorFactor();
                            setTestChartsButtonActivated(true);

                            if (!isCombinationParent) {
                                refreshGetTestMarketIndicatorFactorData();
                            }
                        }}
                        disabled={testButtonDisabled}
                        title={translations.actions.test}
                        type={LinkButtonType.White}
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'TestClicked' }}
                    />
                </div>

                <div className={styles.indicator_add_and_edit_save_and_cancel}>
                    <LinkButton
                        onClick={navigateBackToMarketIndicators}
                        title={translations.actions.cancel}
                        type={LinkButtonType.White}
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'CancelClicked' }}
                    />
                    <LinkButton
                        onClick={props.handleSave}
                        disabled={props.savingDisabled}
                        title={translations.actions.save}
                        type={LinkButtonType.White}
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'SaveClicked' }}
                    />
                    <LinkButton
                        onClick={props.handleSaveAndReturn}
                        disabled={props.savingDisabled}
                        title={translations.actions.saveAndReturn}
                        type={LinkButtonType.Blue}
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'SaveAndReturnClicked' }}
                    />
                </div>
            </div>
            <div className={styles.indicator_add_and_edit_markets_chart}>
                <MarketIndicatorChart
                    runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                    runTestMarketIndicatorFactorResponse={runTestMarketIndicatorFactorResponse!}
                    useColorPalletteInLegend
                    yAxisLabel={transformationType}
                    chartNavigatorTimeRange={DemeterFilterTimeSpan.ThreeMonths}
                    track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                />
            </div>
            {testChartButtonActiveAndValidGetTestFactorResponse && !templateTypeIsCombination && !templateTypeIsManualEntry && !templateTypeIsSpeculative && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorChart
                        title={chartDisplayName}
                        runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                        ignorePlotbands
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                    />
                </div>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && templateTypeIsSpeculative && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorChart
                        title={chartDisplayName}
                        runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                        ignorePlotbands
                        useProjectionChart
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                    />
                </div>
            )}
            {testChartsButtonActivated && currentMarketIndicatorSubfactors && templateTypeIsCombination && searchParameters.tab === 'parent' && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorCombinationChart
                        title={chartDisplayName}
                        runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                        getMarketIndicatorSubFactorResponses={currentMarketIndicatorSubfactors as GetMarketIndicatorFactorResponse[]}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                        disableTooltip={hasRestrictedDataSource}
                    />
                </div>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && templatTypeIsHistorical && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorHistoricalChart
                        title={chartDisplayName}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        parameters={props.upsertMarketIndicatorFactorRequest.parameters as HistoricalParameters}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                    />
                </div>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && (templateTypeIsSeasonal || templateTypeIsSlidingWindowSeasonal) && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorSeasonalChart
                        title={chartDisplayName}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        parameters={props.upsertMarketIndicatorFactorRequest.parameters as SeasonalParameters}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                    />
                </div>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && templateTypeIsAverageCountOrAverageSeasonal && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorAverageSeasonalChart
                        title={chartDisplayName}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        runMarketIndicatorResponse={templateTypeIsAverageCount ? runTestMarketIndicatorFactorResponse : undefined}
                        templateTypeIsAverageCount={templateTypeIsAverageCount}
                        parameters={props.upsertMarketIndicatorFactorRequest.parameters as AverageSeasonalParameters}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                    />
                </div>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && templateTypeIsTechnical && (
                <>
                    <div className={styles.indicator_add_and_edit_markets_chart}>
                        <MarketIndicatorTechnicalChart
                            title={chartDisplayName}
                            runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                            parameters={props.upsertMarketIndicatorFactorRequest.parameters as TechnicalParameters}
                            yAxisLabel={transformationType}
                            isLoading={getTestMarketIndicatorFactorDataLoading}
                        />
                    </div>
                    <div className={styles.indicator_add_and_edit_markets_chart}>
                        <MarketIndicatorChart
                            title={chartDisplayName}
                            runMarketIndicatorResponse={props.getMarketIndicatorResponse}
                            runTestMarketIndicatorFactorResponse={runTestMarketIndicatorFactorResponse}
                            isLoading={getTestMarketIndicatorFactorDataLoading}
                            secondaryRunTestMarketIndicatorFactorResponse={secondaryRunTestMarketIndicatorFactorResponse}
                            useColorPalletteInLegend
                            chartNavigatorTimeRange={DemeterFilterTimeSpan.ThreeMonths}
                            track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                        />
                    </div>
                </>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && templateTypeIsDecile && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorDecileChart
                        title={chartDisplayName}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        parameters={props.upsertMarketIndicatorFactorRequest.parameters as DecileParameters}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                    />
                </div>
            )}
            {testChartButtonActiveAndValidGetTestFactorResponse && templateTypeIsForecast && (
                <div className={styles.indicator_add_and_edit_markets_chart}>
                    <MarketIndicatorForecastChart
                        title={chartDisplayName}
                        runTestMarketIndicatorFactorResponse={getTestMarketIndicatorFactorDataResponse}
                        parameters={props.upsertMarketIndicatorFactorRequest.parameters as ForecastParameters}
                        yAxisLabel={transformationType}
                        isLoading={getTestMarketIndicatorFactorDataLoading}
                    />
                </div>
            )}

            {testChartButtonActiveAndValidGetTestFactorResponse &&
                !!(props.upsertMarketIndicatorFactorRequest.parameters as ManualEntryParameters).imageUrl &&
                (getTestMarketIndicatorFactorDataLoading ? (
                    <LoadingSpinner />
                ) : (
                    <img
                        className={styles.indicator_add_and_edit_image}
                        src={(props.upsertMarketIndicatorFactorRequest.parameters as ManualEntryParameters).imageUrl!}
                        alt={chartDisplayName}
                    />
                ))}
        </div>
    );
};

export default MarketIndicatorFactorCharts;
